import React, { useState, useEffect } from "react";
import { Button, Modal, Typography, Progress } from "antd";
import {
  SafetyCertificateOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  LockOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

// ─── Types ────────────────────────────────────────────────────────────────────

interface MFASetupReminderProps {
  /** Render inside a Modal (default) or as a standalone page block */
  mode?: "modal" | "page";
  /** Controls modal visibility when mode="modal" */
  open?: boolean;
  /** Called when modal requests close (skip/outside click) */
  onClose?: () => void;
  /** Route or handler when user clicks "Set Up Now" */
  onSetup?: () => void;
  /** Route or handler when user clicks "Remind Me Later" */
  onSkip?: () => void;
  /** User's display name */
  userName?: string;
  enForceMFA?: boolean
}

interface MFAContentProps extends Omit<MFASetupReminderProps, "mode" | "open"> {
  skipLoading: boolean;
  setupLoading: boolean;
}

// ─── Security-strength pill ───────────────────────────────────────────────────

const SecurityMeter: React.FC<{ enabled: boolean }> = ({ enabled }) => (
  <div style={styles.meterWrap}>
    <div style={styles.meterHeader}>
      <Text style={styles.meterLabel}>Account security strength</Text>
      <Text style={{ ...styles.meterLabel, color: enabled ? "#52c41a" : "#fa8c16", fontWeight: 600 }}>
        {enabled ? "Strong" : "Moderate"}
      </Text>
    </div>
    <Progress
      percent={enabled ? 100 : 45}
      showInfo={false}
      strokeColor={enabled ? { from: "#52c41a", to: "#73d13d" } : { from: "#fa8c16", to: "#ffc53d" }}
      trailColor="#f0f0f0"
      strokeLinecap="round"
      size={["100%", 6] as any}
    />
  </div>
);

// ─── Benefit row ─────────────────────────────────────────────────────────────

const BenefitRow: React.FC<{ text: string; delay: number }> = ({ text, delay }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div
      style={{
        ...styles.benefitRow,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : "translateX(-12px)",
        transition: "opacity 0.4s ease, transform 0.4s ease",
      }}
    >
      <CheckCircleOutlined style={styles.benefitIcon} />
      <Text style={styles.benefitText}>{text}</Text>
    </div>
  );
};

// ─── Core card content ────────────────────────────────────────────────────────

const MFAContent: React.FC<Omit<MFAContentProps, "mode" | "open">> = ({
  onSetup,
  onSkip,
  userName,
  skipLoading,
  setupLoading,
  enForceMFA = false
}) => {
  const [pulse, setPulse] = useState(false);
  const isAnyLoading = skipLoading || setupLoading;
  
  useEffect(() => {
    const interval = setInterval(() => {
      setPulse((p) => !p);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const benefits = [
    "Blocks 99.9% of automated account attacks",
    "Instant alerts on suspicious sign-in attempts",
    "One-tap approval, takes less than 5 seconds",
  ];

  return (
    <div style={styles.card}>
      {/* Shield icon */}
      <div style={styles.iconRing}>
        <div
          style={{
            ...styles.iconPulse,
            transform: pulse ? "scale(1.15)" : "scale(1)",
            opacity: pulse ? 0 : 0.3,
          }}
        />
        <div style={styles.iconInner}>
          <SafetyOutlined style={styles.shieldIcon} />
        </div>
      </div>

      {/* Headline */}
      <div style={styles.textBlock}>
        <h2 style={styles.heading}>
          {userName ? `${userName}, secure` : "Secure"} your account
        </h2>
        <p style={styles.subtext}>
          Two-factor authentication adds a second layer of protection. Even if
          your password leaks, attackers can't get in.
        </p>
      </div>

      {/* Security meter */}
      <SecurityMeter enabled={false} />

      {/* Benefits */}
      <div style={styles.benefitList}>
        {benefits.map((b, i) => (
          <BenefitRow key={i} text={b} delay={i * 120 + 100} />
        ))}
      </div>

      {/* CTAs */}
      <div style={styles.actions}>
        <Button
          type="primary"
          size="large"
          icon={<LockOutlined />}
          style={styles.primaryBtn}
          onClick={onSetup}
          disabled={isAnyLoading}
        >
          Set Up Now
          <ArrowRightOutlined style={{ marginLeft: 4, fontSize: 12 }} />
        </Button>

        { enForceMFA == false && (
         <button
          style={{
            ...styles.skipBtn,
            opacity: isAnyLoading ? 0.6 : 1,
            cursor: isAnyLoading ? "not-allowed" : "pointer",
          }}
          onClick={onSkip}
          disabled={isAnyLoading}
        >
          {skipLoading ? (
            <>
              <LoadingOutlined spin style={{ marginRight: 8, fontSize: 13, color: "#6b7280" }} />
              Saving preference...
            </>
          ) : (
            "Remind me later"
          )}
        </button>
        )}
      </div>

      {/* Fine print */}
      <Text style={styles.finePrint}>
        <SafetyCertificateOutlined style={{ marginRight: 4, fontSize: 11 }} />
        Setup takes under 2 minutes · Works with any authenticator app
      </Text>
    </div>
  );
};

// ─── Main exported component ──────────────────────────────────────────────────

const MFASetupReminder: React.FC<MFASetupReminderProps> = ({
  mode = "modal",
  open = true,
  onClose,
  onSetup,
  onSkip,
  userName,
  enForceMFA = false
}) => {
  const [skipLoading, setSkipLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const handleSetup = () => {
    onSetup?.();
    // e.g. router.push('/settings/mfa')
  };

  const handleSkip = async () => {
    setSkipLoading(true);
    try {
      await onSkip?.();  // waits for Firestore write to complete
    } finally {
      setSkipLoading(false);
    }
    // e.g. router.push('/dashboard')
  };

  const handleMFAClose = () => {
    onClose?.();
    // e.g. router.push('/dashboard')
  };

  const contentProps = { onSetup: handleSetup, onSkip: handleSkip, userName, skipLoading, setupLoading, enForceMFA };

  if (mode === "page") {
    return (
      <div style={styles.pageWrapper}>
        <MFAContent {...contentProps} />
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={handleMFAClose}
      closable={enForceMFA ? false : true}
      footer={null}
      centered
      width={460}
      closeIcon={
        <CloseOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />
      }
      maskClosable={enForceMFA ? false : !skipLoading}
      keyboard={false}
      styles={{
        content: {
          background: "#ffffff",
          borderRadius: 20,
          padding: 0,
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
        },
        mask: {
          backdropFilter: "blur(4px)",
          background: "rgba(0,0,0,0.35)",
        },
      }}
    >
      <MFAContent {...contentProps} />
    </Modal>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  pageWrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f5f7fa",
    padding: 24,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e8ecf0",
    borderRadius: 20,
    padding: "36px 32px 28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 24,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    position: "relative",
    overflow: "hidden",
  },
  iconRing: {
    position: "relative",
    width: 88,
    height: 88,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iconPulse: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(24,144,255,0.15) 0%, transparent 70%)",
    transition: "transform 1.8s ease, opacity 1.8s ease",
  },
  iconInner: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)",
    border: "2px solid #91caff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 16px rgba(24,144,255,0.15)",
    zIndex: 1,
  },
  shieldIcon: {
    fontSize: 32,
    color: "#1677ff",
  },
  textBlock: {
    textAlign: "center",
    maxWidth: 340,
  },
  heading: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "#111827",
    letterSpacing: "-0.4px",
    lineHeight: 1.25,
  },
  subtext: {
    margin: "10px 0 0",
    fontSize: 14,
    color: "#6b7280",
    lineHeight: 1.65,
  },
  meterWrap: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  meterHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  meterLabel: {
    fontSize: 12,
    color: "#9ca3af",
    letterSpacing: "0.3px",
  },
  benefitList: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  benefitRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  benefitIcon: {
    color: "#22c55e",
    fontSize: 14,
    flexShrink: 0,
  },
  benefitText: {
    fontSize: 13.5,
    color: "#374151",
    lineHeight: 1.4,
  },
  actions: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  primaryBtn: {
    width: "100%",
    height: 48,
    borderRadius: 12,
    background: "#1677ff",
    border: "none",
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: "-0.2px",
    boxShadow: "0 4px 14px rgba(22,119,255,0.3)",
  },
  skipBtn: {
    all: "unset",
    cursor: "pointer",
    width: "100%",
    height: 44,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    color: "#6b7280",
    fontSize: 14,
    textAlign: "center",
    transition: "color 0.2s, border-color 0.2s, background 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  finePrint: {
    fontSize: 11.5,
    color: "#9ca3af",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

export default MFASetupReminder;